import { motion } from "framer-motion";
import MainLayout from "../layout/main-layout";
import { useEffect, useState } from "react";
import axios from "axios";
import Loader from "../Components/loader/loader";
import BlogHeaderSection from "../Components/blog-users/blog-header-section";
import BlogCard from "../Components/blog-users/blog-card";
import { useNavigate } from "react-router-dom";
import MultiSelect from "../Components/input/multi-select";
import { Filter, X } from "lucide-react";

function UserBlog() {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [filteredWorks, setFilteredWorks] = useState<any>([]);
  const [visibleWorks, setVisibleWorks] = useState(4);
  const [selectedIndustries, setSelectedIndustries] = useState<
    (string | number)[]
  >([]);
  const [selectedSort, setSelectedSort] = useState<string | number | null>(
    null
  );
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = () => {
    axios
      .get(`/blogs/articles`, {
        params: {
          is_article: false,
        },
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setBlogs(res.data);
        } else {
          setBlogs([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const industries = Array.from(
    new Set(blogs.map((data: any) => data?.industry).filter(Boolean))
  ).map((domain) => ({
    name: domain,
    label: domain,
    value: domain,
  }));

  const sortOnDuration = [
    { name: "Latest", label: "Latest", value: "latest" },
    { name: "Oldest", label: "Oldest", value: "oldest" },
  ];

  useEffect(() => {
    let filtered = [...blogs];

    if (selectedIndustries.length > 0) {
      filtered = filtered.filter((work: any) =>
        selectedIndustries.includes(work.industry)
      );
    }

    if (selectedSort) {
      filtered.sort((a: any, b: any) => {
        if (selectedSort === "latest") {
          return (
            new Date(b.created_at || b.id).getTime() -
            new Date(a.created_at || a.id).getTime()
          );
        } else if (selectedSort === "oldest") {
          return (
            new Date(a.created_at || a.id).getTime() -
            new Date(b.created_at || b.id).getTime()
          );
        }
        return 0;
      });
    }
    setFilteredWorks(filtered);
    setVisibleWorks(4);
  }, [selectedIndustries, selectedSort,blogs]);

  const clearFilters = () => {
    setSelectedIndustries([]);
    setSelectedSort(null);
  };

  const hasActiveFilters =
    selectedIndustries.length > 0 || selectedSort !== null;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowFilters(true);
      } else {
        setShowFilters(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <MainLayout>
      <section className="min-h-screen h-full w-full p-8 md:p-16 overflow-hidden relative flex flex-col items-center justify-center">
        {loading ? (
          <Loader />
        ) : (
          <>
            <BlogHeaderSection
              title="Blogs"
              message="Discover expert insights, industry trends, and practical tips to stay ahead in your field. Explore engaging content designed to inform, inspire, and empower your journey."
            />
            <div className="relative flex flex-col justify-center items-center w-full max-w-6xl mt-32">
              <div className="md:hidden w-full mb-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    hasActiveFilters
                      ? "bg-blue-50 border-blue-200 text-blue-700"
                      : "bg-white border-gray-300 text-gray-700"
                  } hover:bg-gray-50`}
                >
                  <Filter size={18} />
                  <span>
                    {showFilters ? "Hide Filters" : "Show Filters"}
                    {hasActiveFilters && " (Active)"}
                  </span>
                </button>
              </div>
              <div className="flex flex-col items-center justify-center">
                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                    <MultiSelect
                      options={industries}
                      selectedValues={selectedIndustries}
                      onChange={(val: any) => setSelectedIndustries(val)}
                      labelKey="label"
                      valueKey="value"
                      placeholder="Select Industry"
                      className="w-full md:w-52"
                    />
                    <MultiSelect
                      options={sortOnDuration}
                      selectedValue={selectedSort}
                      onChange={(val: any) => setSelectedSort(val)}
                      labelKey="label"
                      valueKey="value"
                      placeholder="Sort by"
                      className="w-full md:w-52"
                      multiple={false}
                    />
                  </div>
                )}
                <div className="flex items-center gap-4 my-5">
                  {hasActiveFilters && (
                    <motion.button
                      onClick={clearFilters}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X size={16} />
                      Clear Filters
                    </motion.button>
                  )}
                </div>
              </div>
            </div>
            {filteredWorks.length <= 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                    No Blogs Yet
                  </h3>
                  <p className="text-gray-600 max-w-md">
                    There are currently no blog posts to display. Check back
                    later for new content!
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-5">
                  {filteredWorks.slice(0, visibleWorks).map((data: any, index:number) => (
                    <BlogCard
                      data={data}
                      index={index}
                      navigateButtonText="View Blog"
                      handleNavigate={() =>
                        navigate(`/blogs/${data?.title}/${data.id}`)
                      }
                    />
                  ))}
                </div>
                {visibleWorks < filteredWorks.length && (
                  <motion.button
                    onClick={() => setVisibleWorks((prev) => prev + 2)}
                    className="mt-8 px-6 py-3 text-lg font-semibold text-white bg-primary rounded-full shadow-md hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:outline-none transition"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    View More
                  </motion.button>
                )}
              </>
            )}
          </>
        )}
      </section>
    </MainLayout>
  );
}

export default UserBlog;
